if (!customElements.get('card-qty-stepper')) {
  class CardQtyStepper extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.input = this.querySelector('input[type="number"]');
      this.minusBtn = this.querySelector('[data-action="minus"]');
      this.plusBtn = this.querySelector('[data-action="plus"]');

      if (!this.input) return;

      this._clickHandler = this.handleClick.bind(this);
      this._changeHandler = this.updateButtonStates.bind(this);

      this.addEventListener('click', this._clickHandler);
      this.input.addEventListener('change', this._changeHandler);
      this.updateButtonStates();
    }

    disconnectedCallback() {
      if (this._clickHandler) this.removeEventListener('click', this._clickHandler);
      if (this.input && this._changeHandler) this.input.removeEventListener('change', this._changeHandler);
    }

    handleClick(evt) {
      const btn = evt.target.closest('button[data-action]');
      if (!btn) return;

      evt.preventDefault();
      evt.stopPropagation();

      const action = btn.dataset.action;
      const min = parseInt(this.input.min) || 1;
      const max = parseInt(this.input.max) || 999;
      const step = parseInt(this.input.step) || 1;
      let currentVal = parseInt(this.input.value) || min;

      if (action === 'plus') {
        if (currentVal + step <= max) {
          this.input.value = currentVal + step;
        }
      } else if (action === 'minus') {
        if (currentVal - step >= min) {
          this.input.value = currentVal - step;
        }
      }

      this.input.dispatchEvent(new Event('change', { bubbles: true }));
      this.updateButtonStates();
    }

    updateButtonStates() {
      const val = parseInt(this.input.value) || 1;
      const min = parseInt(this.input.min) || 1;
      const max = parseInt(this.input.max) || 999;

      if (this.minusBtn) {
        this.minusBtn.disabled = val <= min;
      }
      if (this.plusBtn) {
        this.plusBtn.disabled = val >= max;
      }
    }

    reset() {
      if (this.input) {
        this.input.value = parseInt(this.input.min) || 1;
        this.updateButtonStates();
      }
    }
  }

  customElements.define('card-qty-stepper', CardQtyStepper);
}

if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });

        // Global delegated click listener for close button & backdrop
        this.addEventListener('click', (event) => {
          if (
            event.target === this ||
            event.target.closest('[id^="ModalClose-"], .quick-add-modal__toggle, .quick-add-modal__close-btn')
          ) {
            event.preventDefault();
            event.stopPropagation();
            this.hide();
          }
        });

        // ESC key listener
        this.addEventListener('keyup', (event) => {
          if (event.code && event.code.toUpperCase() === 'ESCAPE') {
            event.preventDefault();
            this.hide();
          }
        });
      }

      connectedCallback() {
        super.connectedCallback?.();
        this.attachCloseListeners();
      }

      attachCloseListeners() {
        this.querySelectorAll('[id^="ModalClose-"], .quick-add-modal__toggle').forEach((btn) => {
          btn.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.hide();
          };
        });
      }

      hide(preventFocus = false) {
        try {
          const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          if (cartNotification && typeof cartNotification.setActiveElement === 'function') {
            cartNotification.setActiveElement(this.openedBy);
          }
        } catch (e) {
          console.warn('cartNotification error in hide:', e);
        }

        if (this.modalContent) {
          this.modalContent.innerHTML = '';
        }

        if (preventFocus) this.openedBy = null;

        document.body.classList.remove('overflow-hidden');
        document.body.dispatchEvent(new CustomEvent('modalClosed'));
        this.removeAttribute('open');

        try {
          if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openedBy);
        } catch (e) {}

        try {
          if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
        } catch (e) {}
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner')?.classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
            this.attachCloseListeners();
            this.initGallery();
            this.initProductForm();
          })
          .catch((err) => {
            console.error('QuickAdd load error:', err);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      initProductForm() {
        if (!this.modalContent) return;
        const pf = this.modalContent.querySelector('product-form');
        if (pf && typeof pf.init === 'function') {
          pf.init();
        }

        // Safety net: intercept native form submission in popup
        const form = this.modalContent.querySelector('form[action*="/cart/add"]');
        if (form && !form._hasAjaxSubmit) {
          form._hasAjaxSubmit = true;
          form.addEventListener('submit', (e) => {
            if (!e.defaultPrevented) {
              e.preventDefault();
              e.stopPropagation();
              const submitBtn = form.querySelector('[type="submit"]');
              const submitBtnText = submitBtn ? (submitBtn.querySelector('span') || submitBtn) : null;
              if (submitBtn) {
                submitBtn.setAttribute('aria-disabled', true);
                submitBtn.classList.add('loading');
              }

              const formData = new FormData(form);
              formData.append('sections', 'cart-drawer,cart-icon-bubble');
              formData.append('sections_url', window.location.pathname);
              const config = {
                method: 'POST',
                headers: {
                  'X-Requested-With': 'XMLHttpRequest',
                  'Accept': 'application/javascript'
                },
                body: formData
              };

              fetch('/cart/add.js', config)
                .then((r) => r.json())
                .then((response) => {
                  // Cập nhật số lượng giỏ hàng trên Header
                  fetch('/cart.js')
                    .then((r) => r.json())
                    .then((cartData) => {
                      document.querySelectorAll('.cart-badge-count, .cart-count-bubble, [data-cart-count], [id^="CartBadgeCount"]').forEach((el) => {
                        const countSpan = el.querySelector('[aria-hidden="true"]') || el;
                        countSpan.textContent = cartData.item_count;
                        el.classList.remove('hidden');
                      });
                    })
                    .catch(() => {});

                  // Cập nhật và mở Cart Drawer sau khi popup đóng mượt mà
                  const openDrawer = () => {
                    const cartDrawer = document.querySelector('cart-drawer');
                    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
                      if (response.sections) {
                        cartDrawer.renderContents(response);
                      } else {
                        fetch('/cart?section_id=cart-drawer')
                          .then((r) => r.text())
                          .then((html) => {
                            cartDrawer.renderContents({
                              id: response.id,
                              sections: { 'cart-drawer': html }
                            });
                          })
                          .catch(() => {
                            if (typeof cartDrawer.open === 'function') cartDrawer.open();
                          });
                      }
                    } else if (cartDrawer && typeof cartDrawer.open === 'function') {
                      cartDrawer.open();
                    }
                  };

                  document.body.addEventListener('modalClosed', () => {
                    setTimeout(openDrawer, 50);
                  }, { once: true });

                  this.hide(true);

                  // Phản hồi nút thành công tại popup
                  if (submitBtn) {
                    submitBtn.classList.remove('loading');
                    submitBtn.removeAttribute('aria-disabled');
                    submitBtn.classList.add('atc-success');
                    const orig = submitBtnText ? submitBtnText.innerHTML : submitBtn.innerHTML;
                    if (submitBtnText) {
                      submitBtnText.innerHTML = `
                        <span style="display:inline-flex;align-items:center;gap:6px;">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                          ADDED TO CART
                        </span>
                      `;
                    }
                    setTimeout(() => {
                      submitBtn.classList.remove('atc-success');
                      if (submitBtnText) submitBtnText.innerHTML = orig;
                    }, 2500);
                  }
                })
                .catch((err) => {
                  console.error('Safety net ATC error:', err);
                  if (submitBtn) {
                    submitBtn.classList.remove('loading');
                    submitBtn.removeAttribute('aria-disabled');
                  }
                });
            }
          });
        }
      }

      initGallery() {
        if (!this.modalContent) return;

        const slides = Array.from(this.modalContent.querySelectorAll('.product__media-item'));
        const thumbnails = Array.from(this.modalContent.querySelectorAll('.thumbnail-list__item'));
        const prevBtn = this.modalContent.querySelector('.product-gallery-nav-btn--prev');
        const nextBtn = this.modalContent.querySelector('.product-gallery-nav-btn--next');
        const arrowsWrap = this.modalContent.querySelector('.product-gallery-nav-arrows');
        const thumbSlider = this.modalContent.querySelector('.thumbnail-slider');

        if (!slides || slides.length === 0) return;

        // If only 1 slide, hide navigation controls
        if (slides.length <= 1) {
          if (arrowsWrap) arrowsWrap.style.display = 'none';
          if (thumbSlider) thumbSlider.style.display = 'none';
          return;
        } else {
          if (arrowsWrap) arrowsWrap.style.display = 'flex';
          if (thumbSlider) thumbSlider.style.display = 'flex';
        }

        // Ensure current active slide
        let currentActive = slides.findIndex((s) => s.classList.contains('is-active'));
        if (currentActive === -1) {
          currentActive = 0;
          slides[0].classList.add('is-active');
        }

        const goToSlide = (targetIdx) => {
          if (targetIdx < 0) targetIdx = slides.length - 1;
          if (targetIdx >= slides.length) targetIdx = 0;

          const targetSlide = slides[targetIdx];
          if (!targetSlide) return;

          slides.forEach((s, idx) => {
            s.classList.toggle('is-active', idx === targetIdx);
          });

          const targetMediaId = targetSlide.getAttribute('data-media-id');

          // Sync thumbnails
          thumbnails.forEach((thumb, idx) => {
            const isMatch = (idx === targetIdx) || (targetMediaId && thumb.getAttribute('data-target') === targetMediaId);
            thumb.classList.toggle('is-active', isMatch);
            const btn = thumb.querySelector('button');
            if (btn) btn.setAttribute('aria-current', isMatch ? 'true' : 'false');
            if (isMatch && typeof thumb.scrollIntoView === 'function') {
              thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
          });

          // Sync with media-gallery custom element if available
          const mg = this.modalContent.querySelector('media-gallery');
          if (mg && typeof mg.setActiveMedia === 'function' && targetMediaId) {
            try { mg.setActiveMedia(targetMediaId, false); } catch (e) {}
          }
        };

        goToSlide(currentActive);

        // Previous button click
        if (prevBtn) {
          prevBtn.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            const idx = Math.max(0, slides.findIndex((s) => s.classList.contains('is-active')));
            goToSlide(idx - 1);
          };
        }

        // Next button click
        if (nextBtn) {
          nextBtn.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            const idx = Math.max(0, slides.findIndex((s) => s.classList.contains('is-active')));
            goToSlide(idx + 1);
          };
        }

        // Thumbnail buttons click
        thumbnails.forEach((thumb, idx) => {
          thumb.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            goToSlide(idx);
          };
        });

        // Touch/Swipe navigation on mobile
        const mediaList = this.modalContent.querySelector('.product__media-list');
        if (mediaList && !mediaList._hasSwipe) {
          mediaList._hasSwipe = true;
          let touchStartX = 0;
          let touchStartY = 0;
          mediaList.addEventListener('touchstart', (e) => {
            touchStartX = e.changedTouches[0].screenX;
            touchStartY = e.changedTouches[0].screenY;
          }, { passive: true });

          mediaList.addEventListener('touchend', (e) => {
            const touchEndX = e.changedTouches[0].screenX;
            const touchEndY = e.changedTouches[0].screenY;
            const diffX = touchEndX - touchStartX;
            const diffY = touchEndY - touchStartY;
            if (Math.abs(diffX) > 40 && Math.abs(diffX) > Math.abs(diffY)) {
              const idx = Math.max(0, slides.findIndex((s) => s.classList.contains('is-active')));
              if (diffX < 0) goToSlide(idx + 1);
              else goToSlide(idx - 1);
            }
          }, { passive: true });
        }

        // Synchronize when a variant is selected
        if (!this.modalContent._hasVariantSync) {
          this.modalContent._hasVariantSync = true;
          this.modalContent.addEventListener('change', (e) => {
            if (e.target.closest('variant-selects, variant-radios')) {
              setTimeout(() => {
                const refreshedSlides = Array.from(this.modalContent.querySelectorAll('.product__media-item'));
                const activeSlide = this.modalContent.querySelector('.product__media-item.is-active');
                if (activeSlide) {
                  const idx = refreshedSlides.indexOf(activeSlide);
                  if (idx >= 0) goToSlide(idx);
                }
              }, 120);
            }
          });
        }
      }


      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
